import React, { useEffect, useRef, useState } from 'react'
import { Button, Icon, Input, Modal, Progress, Tag, Tooltip, Upload } from 'antd'
import './BrandChat.scss'

const { TextArea } = Input

const formatFileSize = size => {
  const value = Number(size)
  if (!Number.isFinite(value) || value <= 0) return ''

  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(1)} MB`
  }

  if (value >= 1024) {
    return `${Math.round(value / 1024)} KB`
  }

  return `${value} B`
}

const getAttachmentFromMessage = item => {
  if (item?.attachment) return item.attachment

  const uploadMatch = String(item?.content || '').match(/^Uploaded\s+(.+)$/i)
  if (!uploadMatch) return null

  return {
    filename: uploadMatch[1],
    status: 'uploaded'
  }
}

const getAttachmentStatus = status => {
  if (status === 'uploading') {
    return { icon: 'loading', label: 'Uploading', tone: 'uploading' }
  }

  if (status === 'failed') {
    return { icon: 'close-circle', label: 'Failed', tone: 'failed' }
  }

  return { icon: 'check-circle', label: 'Uploaded', tone: 'uploaded' }
}

const getPhaseLabel = phase => {
  if (phase === 'confirmation') return 'Confirmation'
  if (phase === 'finalizing') return 'Finalizing'
  if (phase === 'completed') return 'Completed'
  return 'Discovery'
}

const renderInlineFormatting = text => {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g)

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${part}-${index}`}>{part.slice(2, -2)}</strong>
    }

    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={`${part}-${index}`}>{part.slice(1, -1)}</em>
    }

    return part
  })
}

const renderFormattedMessage = text => {
  const lines = String(text || '').split('\n')
  const blocks = []
  let listItems = []
  let listType = 'ul'

  const flushList = () => {
    if (!listItems.length) return

    const ListTag = listType
    blocks.push(
      <ListTag
        className="brand-chat__content-list"
        key={`list-${blocks.length}`}
      >
        {listItems.map((item, index) => (
          <li key={`${item}-${index}`}>{renderInlineFormatting(item)}</li>
        ))}
      </ListTag>
    )
    listItems = []
    listType = 'ul'
  }

  lines.forEach((line, index) => {
    const trimmedLine = line.trim()

    if (!trimmedLine) {
      flushList()
      return
    }

    const bulletMatch = trimmedLine.match(/^[-*]\s+(.+)$/)
    const numberedMatch = trimmedLine.match(/^\d+\.\s+(.+)$/)
    const headingMatch = trimmedLine.match(/^#{1,4}\s+(.+)$/)

    if (headingMatch) {
      flushList()
      blocks.push(
        <h4 key={`heading-${index}`}>
          {renderInlineFormatting(headingMatch[1])}
        </h4>
      )
      return
    }

    if (bulletMatch) {
      if (listItems.length && listType !== 'ul') flushList()
      listType = 'ul'
      listItems.push(bulletMatch[1])
      return
    }

    if (numberedMatch) {
      if (listItems.length && listType !== 'ol') flushList()
      listType = 'ol'
      listItems.push(numberedMatch[1])
      return
    }

    flushList()
    blocks.push(
      <p key={`line-${index}`}>{renderInlineFormatting(trimmedLine)}</p>
    )
  })

  flushList()

  return blocks
}

const BrandChat = ({
  messages,
  metadata,
  uploadedFiles,
  isStreaming,
  isFinalizing,
  isProfileCompleted,
  canFinalize,
  acceptedExtensions = [],
  onBackToSetup,
  onUploadGuidelines,
  onSendMessage,
  onFinalize
}) => {
  const [draftMessage, setDraftMessage] = useState('')
  const [isFinalizeModalVisible, setIsFinalizeModalVisible] = useState(false)
  const [hasConfirmedFinalize, setHasConfirmedFinalize] = useState(false)
  const scrollRef = useRef(null)

  useEffect(() => {
    if (scrollRef.current) {
      window.requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight
        }
      })
    }
  }, [messages, isStreaming])

  const handleSubmit = () => {
    const message = draftMessage.trim()
    if (!message || isStreaming || isFinalizing) return

    setDraftMessage('')
    onSendMessage(message)
  }

  const handleKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const handleOpenFinalizeModal = () => {
    if (!canFinalize || isStreaming || isFinalizing) return

    setHasConfirmedFinalize(false)
    setIsFinalizeModalVisible(true)
  }

  const handleCloseFinalizeModal = () => {
    if (isFinalizing) return

    setIsFinalizeModalVisible(false)
    setHasConfirmedFinalize(false)
  }

  const handleConfirmFinalize = async () => {
    if (isFinalizing) return

    setHasConfirmedFinalize(true)
    const didFinalize = await onFinalize()

    if (!didFinalize) {
      setHasConfirmedFinalize(false)
    }
  }

  const uploadProps = {
    multiple: false,
    showUploadList: false,
    accept: acceptedExtensions.map(extension => `.${extension}`).join(','),
    disabled: isStreaming || isFinalizing || isProfileCompleted,
    beforeUpload: file => {
      if (!isProfileCompleted && onUploadGuidelines) {
        onUploadGuidelines(file)
      }
      return false
    }
  }

  const renderMessageContent = item => {
    const attachment = getAttachmentFromMessage(item)

    if (attachment) {
      const status = getAttachmentStatus(attachment.status)
      const sizeLabel = formatFileSize(attachment.size)

      return (
        <div className="brand-chat__attachment-card">
          <div className="brand-chat__attachment-icon">
            <Icon type="paper-clip" />
          </div>
          <div className="brand-chat__attachment-main">
            <span className="brand-chat__attachment-kicker">
              Brand guideline file
            </span>
            <strong title={attachment.filename}>{attachment.filename}</strong>
            {sizeLabel ? <small>{sizeLabel}</small> : null}
          </div>
          <div
            className={`brand-chat__attachment-status brand-chat__attachment-status--${status.tone}`}
          >
            <Icon type={status.icon} />
            <span>{status.label}</span>
          </div>
        </div>
      )
    }

    return item.role === 'assistant'
      ? renderFormattedMessage(item.content)
      : item.content
  }

  const renderChatMessage = item => {
    const hasAttachment = Boolean(getAttachmentFromMessage(item))

    return (
      <div
        className={`brand-chat__message brand-chat__message--${item.role}`}
        key={item.id}
      >
        <div className="brand-chat__avatar">
          <Icon type={item.role === 'assistant' ? 'robot' : 'user'} />
        </div>
        <div
          className={`brand-chat__bubble${
            hasAttachment ? ' brand-chat__bubble--attachment' : ''
          }`}
        >
          <div className="brand-chat__role">
            {item.role === 'assistant' ? 'Brand assistant' : 'You'}
          </div>
          <div className="brand-chat__content">
            {renderMessageContent(item)}
            {item.streaming ? <span className="brand-chat__cursor" /> : null}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="brand-chat">
      <aside className="brand-chat__sidebar">
        <Button
          className="brand-chat__back"
          onClick={onBackToSetup}
          disabled={isStreaming || isFinalizing}
        >
          <Icon type="left" />
          Setup options
        </Button>

        <div className="brand-chat__guide-card">
          <div className="brand-chat__guide-icon">
            <Icon type="compass" />
          </div>
          <div>
            <span>Brand discovery</span>
            <strong>{getPhaseLabel(metadata.phase)}</strong>
            <p>
              Answer the current question. Sharelook tracks what is captured
              and what still needs definition.
            </p>
          </div>
        </div>

        <div className="brand-chat__status">
          <div className="brand-chat__status-row">
            <div className="brand-chat__status-label">Profile progress</div>
            <strong>{metadata.completionPercentage}%</strong>
          </div>
          <Progress
            percent={metadata.completionPercentage}
            strokeColor="#0f8c6a"
            trailColor="#e5e7eb"
            size="small"
            showInfo={false}
          />
        </div>

        <div className="brand-chat__section">
          <h3>
            <Icon type="check-circle" />
            Captured
          </h3>
          <div className="brand-chat__tags">
            {metadata.topicsCovered.length ? (
              metadata.topicsCovered.map(topic => (
                <Tag key={topic} color="green">
                  {topic.replace(/_/g, ' ')}
                </Tag>
              ))
            ) : (
              <span>No topics confirmed yet</span>
            )}
          </div>
        </div>

        <div className="brand-chat__section">
          <h3>
            <Icon type="clock-circle" />
            Still needed
          </h3>
          <div className="brand-chat__tags">
            {metadata.topicsRemaining.length ? (
              metadata.topicsRemaining.map(topic => (
                <Tag key={topic}>{topic.replace(/_/g, ' ')}</Tag>
              ))
            ) : (
              <span>The assistant will ask what it still needs.</span>
            )}
          </div>
        </div>

        {uploadedFiles.length ? (
          <div className="brand-chat__section">
            <h3>Uploads</h3>
            <div className="brand-chat__files">
              {uploadedFiles.map(file => (
                <div className="brand-chat__file" key={file.file_id || file.filename}>
                  <Icon type="paper-clip" />
                  <span>{file.filename}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="brand-chat__next-card">
          <Icon type="bulb" />
          <span>
            The assistant will move from basics into visual identity, people,
            environments, motion, music, and video defaults.
          </span>
        </div>

        <Button
          type="primary"
          block
          disabled={!canFinalize || isStreaming}
          loading={isFinalizing}
          onClick={handleOpenFinalizeModal}
        >
          Finalize brand profile
        </Button>
      </aside>

      <section className="brand-chat__workspace">
        <div className="brand-chat__messages" ref={scrollRef}>
          {messages.map(renderChatMessage)}

          {isStreaming && !messages.some(item => item.streaming) ? (
            <div className="brand-chat__message brand-chat__message--assistant">
              <div className="brand-chat__avatar">
                <Icon type="robot" />
              </div>
              <div className="brand-chat__bubble">
                <div className="brand-chat__role">Brand assistant</div>
                <div className="brand-chat__typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="brand-chat__composer">
          <div className="brand-chat__composer-input">
            <TextArea
              value={draftMessage}
              rows={3}
              disabled={isStreaming || isFinalizing}
              placeholder="Reply with brand details, preferences, or corrections..."
              onChange={event => setDraftMessage(event.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="brand-chat__composer-actions">
            <Upload {...uploadProps}>
              <Tooltip
                title={
                  isProfileCompleted
                    ? 'Attachments are disabled after the brand profile is completed'
                    : 'Attach brand guidelines'
                }
              >
                <Button
                  className="brand-chat__attach-btn"
                  disabled={
                    isStreaming ||
                    isFinalizing ||
                    isProfileCompleted ||
                    !onUploadGuidelines
                  }
                >
                  <Icon type={isStreaming ? 'loading' : 'paper-clip'} />
                  <span>Attach</span>
                </Button>
              </Tooltip>
            </Upload>
            <Button
              type="primary"
              disabled={!draftMessage.trim() || isStreaming || isFinalizing}
              onClick={handleSubmit}
            >
              Send
              <Icon type="arrow-right" />
            </Button>
          </div>
        </div>
      </section>

      <Modal
        visible={isFinalizeModalVisible}
        closable={!isFinalizing}
        maskClosable={!isFinalizing}
        footer={null}
        width={430}
        className="brand-chat__finalize-modal"
        onCancel={handleCloseFinalizeModal}
      >
        {hasConfirmedFinalize || isFinalizing ? (
          <div className="brand-chat__finalize-state">
            <div className="brand-chat__finalize-loader">
              <Icon type="loading" />
            </div>
            <h2>Finalizing brand profile</h2>
            <p>
              Sharelook is turning the conversation into a reusable brand
              document. This can take up to a minute.
            </p>
            <div className="brand-chat__finalize-steps">
              <span>
                <Icon type="check-circle" />
                Reviewing answers
              </span>
              <span>
                <Icon type="loading" />
                Preparing profile
              </span>
              <span>
                <Icon type="file-text" />
                Building document
              </span>
            </div>
          </div>
        ) : (
          <div className="brand-chat__finalize-confirm">
            <div className="brand-chat__finalize-icon">
              <Icon type="file-done" />
            </div>
            <h2>Finalize brand profile?</h2>
            <p>
              We will create the brand profile, visual guidance, and downloadable
              document from this session.
            </p>
            <div className="brand-chat__finalize-actions">
              <Button onClick={handleCloseFinalizeModal}>Keep editing</Button>
              <Button type="primary" onClick={handleConfirmFinalize}>
                Finalize profile
                <Icon type="arrow-right" />
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default BrandChat
